var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import PushStream from 'zen-push';
import { DEFAULT_FETCH_POLICY, DEFAULT_REQUEST_METHOD } from '../constants';
import { ApiError } from '../errors';
import { GenericCache } from '../generic-cache';
import { apiRequestId, applyHeaders, cloneHeaders } from '../lib';
import { ApiRequestFetcher } from '../request-fetcher';
function identity(value) {
    return value;
}
/**
 * The request manager is responsible for managing API requests.
 *
 * Responsibilities include:
 * - Implementing `getRequestBody` which is called by `Api`
 * - Caching in-progress GET requests
 * - Detecting a JSON request body by stringifying and setting the appropriate Content-Type headers
 * - Serializing / parsing JSON requests & responses
 */
export class ApiRequestManager {
    constructor(params) {
        this.responseBodyStream = new PushStream();
        this.errorStream = new PushStream();
        this.defaultHeaders = {};
        this.inProgressRequestCache = new GenericCache();
        /**
         * Returns a promise that resolves with the response body.
         *
         * If there is an in-progress request, and `options.deduplicate` is `true`,
         * it will return the same promise.
         *
         * On a successful fetch, it will cache the response unless `fetchPolicy` is `'no-cache'`
         */
        this.getResponseBody = (params, options) => {
            var _a;
            const paramsKey = apiRequestId(params);
            // return cached promise if it exists
            const cachedRequest = this.inProgressRequestCache.get(paramsKey);
            const deduplicate = (_a = options.deduplicate, (_a !== null && _a !== void 0 ? _a : defaultDeduplicate(params)));
            if (cachedRequest && deduplicate) {
                // if user hasn't explicitly requested a new fetch,
                // return the in-progress fetch promise
                return cachedRequest.requestPromise;
            }
            const id = Symbol();
            const fetchPromise = this.createRequestPromise(params, options, id);
            this.inProgressRequestCache.set(paramsKey, {
                id,
                requestPromise: fetchPromise
            });
            return fetchPromise;
        };
        /**
         * Returns `true` if a `GET` request matches params
         */
        this.requestInProgress = (params) => {
            return this.inProgressRequestCache.has(apiRequestId(params));
        };
        /**
         * Set default headers to be passed to all API requests.
         * Useful for setting an authentication token.
         *
         * @param key Header key
         * @param value Header value
         */
        this.setDefaultHeader = (key, value) => {
            this.defaultHeaders = applyHeaders(this.defaultHeaders, { [key]: value });
        };
        this.fetchResponseBody = (params, options) => __awaiter(this, void 0, void 0, function* () {
            const { fetchPolicy = DEFAULT_FETCH_POLICY } = options;
            try {
                const { body, headers } = this.getRequestHeadersAndBody(params);
                const response = yield this.requestFetcher.getResponse({
                    method: params.method || DEFAULT_REQUEST_METHOD,
                    url: `${this.baseUrl}${params.url}`,
                    body,
                    headers,
                    responseType: params.responseType,
                    successCodes: params.successCodes
                });
                const parsedResponse = Object.assign(Object.assign({}, response), { body: response.bodyType === 'json'
                        ? this.parseResponseJson(response.body, params)
                        : response.body });
                this.maybeThrowApiError(params, parsedResponse);
                const { body: responseBody } = parsedResponse;
                if (fetchPolicy !== 'no-cache') {
                    this.responseBodyStream.next([params, responseBody]);
                }
                return responseBody;
            }
            catch (error) {
                this.errorStream.next(error);
                throw error;
            }
        });
        this.baseUrl = params.baseUrl || '';
        this.requestFetcher = params.requestFetcher || new ApiRequestFetcher();
        this.serializeRequestJson = params.serializeRequestJson || identity;
        this.parseResponseJson = params.parseResponseJson || identity;
    }
    createRequestPromise(params, options, id) {
        return __awaiter(this, void 0, void 0, function* () {
            const paramsId = apiRequestId(params);
            try {
                return yield this.fetchResponseBody(params, options);
            }
            finally {
                const cachedRequest = this.inProgressRequestCache.get(paramsId);
                if (cachedRequest && cachedRequest.id === id) {
                    this.inProgressRequestCache.del(paramsId);
                }
            }
        });
    }
    /**
     * Configuring an error handler to be called on error
     */
    get onReceivedResponseBody() {
        return this.responseBodyStream.observable;
    }
    /**
     * Configuring an error handler to be called on error
     */
    get onError() {
        return this.errorStream.observable;
    }
    /**
     * Modifies request headers and body to use for the request.
     * - Headers are merged with any defined default headers.
     * - For JSON request bodies, it is serialized to a string and
     *   sets the appropriate 'Content-Type' header.
     */
    getRequestHeadersAndBody(params) {
        let headers = cloneHeaders(this.defaultHeaders);
        if (params.headers) {
            headers = applyHeaders(headers, params.headers);
        }
        let body;
        if (params.method &&
            ['POST', 'PATCH', 'PUT', 'DELETE'].includes(params.method)) {
            const paramBody = params.body;
            if (paramBody &&
                ![Blob, FormData, URLSearchParams, ReadableStream].some((bodyType) => paramBody instanceof bodyType) &&
                typeof paramBody !== 'string') {
                // prepare JSON body and add header
                body = JSON.stringify(this.serializeRequestJson(paramBody, params));
                headers['content-type'] = 'application/json';
            }
            else {
                body = paramBody;
            }
        }
        return { headers, body };
    }
    /**
     * Throws `ApiError` if the response status does not match a passed success code.
     * If no success codes are passed and the fetch response is not 'ok', throws `ApiError`
     */
    maybeThrowApiError(params, { status, body, bodyType }) {
        var _a, _b;
        if (Array.isArray(params.successCodes)) {
            if (params.successCodes.every((code) => status !== code)) {
                throw new ApiError((_a = params.method, (_a !== null && _a !== void 0 ? _a : DEFAULT_REQUEST_METHOD)), params.url, status, body, bodyType);
            }
        }
        else if (status < 200 || status > 299) {
            throw new ApiError((_b = params.method, (_b !== null && _b !== void 0 ? _b : DEFAULT_REQUEST_METHOD)), params.url, status, body, bodyType);
        }
    }
}
function defaultDeduplicate(params) {
    return params.method === 'GET' ? true : false;
}
//# sourceMappingURL=index.js.map